import type { i18n as I18nInstance } from 'i18next';
import soldIcon from '/business/shared/images/icons/bp-fuel-nozzle-pictograms-bp-green-.svg';
import { basketTableTotalTypes, questionScopeCategories, questionSelectorOptions } from '/business/shared/util/Statics';
import type { QuestionConfigBranchLoaderProps, QuestionConfigQuestionType } from '/index';
import formatNumber from '/shared/functions/formatNumber';

import initiativeCardBackground from '/business/shared/images/tip-img-nov2024/Sold product card image.jpg';
import initiativeBackground from '/business/shared/images/tip-img-nov2024/Sold product card image@2x.jpg';

import useFormatSharedCustomScope1 from '/business/shared/components/pages/question/formats/customDataFormats/useFormatSharedCustomScope1';
import useFormatSharedSupplierScope1Ver2 from '/business/shared/components/pages/question/formats/supplierDataFormats/useFormatSharedSupplierScope1Ver2';
import { bottomContentsSupplier } from '/business/shared/components/questions/scope3/_education/bottomScope3';
import educationContentJson from '/business/shared/components/questions/scope3/soldProducts/_education/soldProducts.json';
import useFuelsSupplierSchema from '/business/shared/components/questions/scope3/soldProducts/_hooks/useFuelsSupplierDataSchema';
import ReportingPeriod from '/business/shared/components/widgets/reporting-period/ReportingPeriod';
import StyledInfoFlash from '/shared/styled/StyledInfoFlash';
import StyledParagraph from '/shared/styled/components/StyledParagraph';

export const getConfigureSoldProducts = (_options: { i18n?: I18nInstance } = {}): QuestionConfigQuestionType => {
  return {
    id: 'sold-products-fuels',
    breadcrumbTitle: 'Fuels sold',
    title: 'Use of sold product - fuels sold',
    useHookLoader: useFuelsSupplierSchema,
    apiKey: 'calculation-use-of-sold-product-fuels',
    initiative: {
      getVisuals: () => {
        return {
          description: false,
          background: initiativeBackground,
          useMask: false,
          cardBackground: initiativeCardBackground,
        };
      },
    },
    table: {
      title: 'Calculated carbon emissions for use of sold product: ',
      totalType: basketTableTotalTypes.TONNAGE,
      headings: ['Emission source', 'Quantity', 'Carbon emissions'].filter((item) => !!item),
      cells: [
        'fuelTypeLabel',
        'quantity',
        ({ tonnage }: { tonnage: number }) => `${formatNumber(tonnage)} tCO\u2082e`,
      ].filter((item) => !!item),
      isAnswerEditable: true,
      tabName: 'Fuels sold',
      mainQuestionTitle: 'Calculated carbon emissions for use of sold fuels: ',
      supplier: {
        columns: [
          'emissionSource',
          'supplierName',
          'supplierIntensityDataOrAbsoluteEmissions',
          'quantity',
          'carbonEmissions',
        ],
      },
      custom: {
        columns: ['emissionSource', 'intensityValue', 'unitOfMeasurement', 'quantity', 'carbonEmissions'],
      },
    },
    scopeCategory: questionScopeCategories.SCOPE_3,
    primaryI18nKey: 'business:QuestionPage.forms.emissionSelector.useOfSoldProducts',
    subTextI18nKey: 'business:QuestionPage.forms.emissionSelector.useOfSoldProductsTip',
    overviewPageTitleI18nKey: 'business:QuestionPage.forms.emissionSelector.useOfSoldProducts',
    icon: soldIcon,
    isCompletePropName: 'isUseOfSoldProductComplete',
    selector: {
      injectQuestionBranchSelector: true,
      questionBranchEnableCustomData: true,
      questionBranchEnableSupplierData: true,
      para: 'To calculate emissions from the use of fuels sold by your business, you can use a combination of activity data, supplier data, and custom data.',
      options: [
        {
          title: questionSelectorOptions.ACTIVITY,
        },
        {
          title: questionSelectorOptions.SUPPLIER,
        },
        {
          title: questionSelectorOptions.CUSTOM,
        },
      ],
    },
    education: {
      hasEducationWidget: true,
      educationContent: educationContentJson,
      bottomContentsSupplier: bottomContentsSupplier,
    },
    useQuestionBranchSupplierLoader: (props: QuestionConfigBranchLoaderProps) =>
      useFormatSharedSupplierScope1Ver2({
        ...props,
        questionFormatName: 'sold-products-fuels',
        emissionSourcePlaceholder: 'Select type of fuel',
        sourceErrMsg: 'Please select a type of fuel',
      }),
    useQuestionBranchCustomLoader: (props: QuestionConfigBranchLoaderProps) =>
      useFormatSharedCustomScope1({ ...props, questionFormatName: 'sold-products-fuels' }),
    supplierAndCustomSubheader: (
      <>
        <StyledParagraph $largeText $hasExtraMarginBottom>
          Enter data about fuels that have been sold by your business during the reporting period:{' '}
          <ReportingPeriod $green />
        </StyledParagraph>
        <StyledInfoFlash>
          <div>For more information, refer to the "Help" section for detailed guidance.</div>
        </StyledInfoFlash>
      </>
    ),
    activitySubheader: (
      <>
        <StyledParagraph $largeText $hasExtraMarginBottom>
          What fuels have been sold by your business during the reporting period: <ReportingPeriod $green />
        </StyledParagraph>
        <StyledInfoFlash>
          <div>For more information, refer to the "Help" section for detailed guidance.</div>
        </StyledInfoFlash>
      </>
    ),
  };
};
